import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card } from '@kss/ui';
import { authService } from '@kss/api';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

/**
 * Email-verification landing page.
 *
 * The customer arrives here from the verification email link
 * (`/verify-email?token=<jwt>`). We POST the token to
 * `/api/auth/verify-email` immediately on mount and show a clear
 * outcome — success, failure, or "no token in URL".
 *
 * We deliberately don't auto-redirect on success so the customer
 * sees the confirmation screen and clicks through to sign-in
 * deliberately. Some email clients pre-fetch links — surfacing
 * success vs. failure gives the user something to do (or not do)
 * based on what their inbox already triggered.
 */
type Stage = 'idle' | 'verifying' | 'success' | 'error';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? searchParams.get('code') ?? '').trim();
  const [stage, setStage] = useState<Stage>(token ? 'verifying' : 'idle');
  const [error, setError] = useState<string>('');
  // Guard against React StrictMode mounting twice in dev — without
  // this guard the same single-use token is sent twice and the
  // second request always fails.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    setStage('verifying');
    (async () => {
      try {
        await authService.verifyEmail(token);
        setStage('success');
      } catch (e) {
        setStage('error');
        setError(e instanceof Error ? e.message : 'Verification failed.');
      }
    })();
  }, [token]);

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <Card className="grid gap-3 p-6 text-center">
        {!token && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-content-muted" />
            <h1 className="text-lg font-bold">Verification link invalid</h1>
            <p className="text-sm text-content-secondary">
              This URL is missing a verification token. Use the link from
              your most recent verification email.
            </p>
            <Link
              to="/"
              className="text-sm font-medium text-primary hover:underline"
            >
              ← Back to home
            </Link>
          </>
        )}

        {stage === 'verifying' && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="text-lg font-bold">Verifying your email…</h1>
            <p className="text-sm text-content-secondary">
              This will only take a moment.
            </p>
          </>
        )}

        {stage === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
            <h1 className="text-lg font-bold">Email verified</h1>
            <p className="text-sm text-content-secondary">
              Your email is now confirmed. You can sign in to access your
              account.
            </p>
            <Button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('kss:open-login'));
              }}
            >
              Sign in
            </Button>
          </>
        )}

        {stage === 'error' && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-danger" />
            <h1 className="text-lg font-bold">Verification failed</h1>
            <Alert variant="danger" className="text-left">
              {error || 'The link is invalid, expired, or already used.'}
            </Alert>
            <p className="text-xs text-content-secondary">
              Try signing in — if your email needs another verification
              email, the sign-in screen will offer one.
            </p>
            <Link
              to="/"
              className="text-sm font-medium text-primary hover:underline"
            >
              ← Back to home
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
